import { isWasteContainerTypeCode, WasteContainerTypeCode } from "../constants/wasteContainerTypes";
import { INHALATION_HAZARD_MARKING } from "./hazardousWasteLabelVisuals";

/**
 * Which set of DOT markings a container carries, by its container-type code.
 * The three groups partition the 13 codes in `wasteContainerTypes.ts` with no
 * remainder. Which marking each group takes: docs/waste/dot-label-rendering.md.
 */
export type DotMarkingGroup = "PACKAGE" | "BULK" | "PORTABLE_TANK";

/**
 * The group a code falls in when nothing is known about how much it holds.
 * Every code except TP can hold either side of the bulk threshold in
 * principle, so this is the historical reading, not the answer.
 */
const GROUP_BY_CODE: Record<WasteContainerTypeCode, DotMarkingGroup> = {
  BA: "PACKAGE",
  CF: "PACKAGE",
  CW: "PACKAGE",
  CY: "PACKAGE",
  DF: "PACKAGE",
  DM: "PACKAGE",
  DW: "PACKAGE",
  CM: "BULK",
  DT: "BULK",
  HG: "BULK",
  TC: "BULK",
  TT: "BULK",
  TP: "PORTABLE_TANK",
};

/**
 * Codes that name a vehicle or a rail car. Nothing they hold is small enough
 * to be a non-bulk package, so capacity does not come into it.
 */
const ALWAYS_BULK: WasteContainerTypeCode[] = ["DT", "HG", "TC", "TT"];

/** Gallons per unit, for the units that measure volume. */
const GALLONS_PER_UNIT: Record<string, number> = {
  GALLONS: 1,
  LITERS: 0.2641720524,
  BARRELS: 42,
  CUBIC_FEET: 7.480519481,
};

/** Pounds per unit, for the units that measure mass. */
const POUNDS_PER_UNIT: Record<string, number> = {
  POUNDS: 1,
  KILOGRAMS: 2.20462262,
  TONS: 2000,
  METRIC_TONS: 2204.62262,
};

/**
 * The 49 CFR 171.8 bulk thresholds. A container at or under its threshold is
 * a non-bulk package.
 *
 * The regulation states the solid case as net mass over 400kg AND capacity
 * over 450L. Only one of the two is usually recorded, so the measure that is
 * present decides. That is the reading the compliance reviewer gave.
 */
const BULK_OVER_GALLONS = 119;
const BULK_OVER_POUNDS_SOLID = 882;
const BULK_OVER_POUNDS_GAS_WATER_CAPACITY = 1000;

/** What the container holds in gallons, or undefined when it is not recorded
 * in a unit of volume. */
function gallonsOf(
  capacity: number | null | undefined,
  capacityUnit: string | null | undefined
): number | undefined {
  if (capacity === null || capacity === undefined || !capacityUnit) {
    return undefined;
  }
  const gallonsPer = GALLONS_PER_UNIT[capacityUnit.trim().toUpperCase()];
  return gallonsPer === undefined ? undefined : capacity * gallonsPer;
}

/**
 * Whether what the container holds puts it over the bulk threshold, or
 * undefined when the recorded capacity cannot answer.
 */
function exceedsBulkThreshold(
  capacity: number | null | undefined,
  capacityUnit: string | null | undefined,
  physicalState: string | null | undefined
): boolean | undefined {
  if (capacity === null || capacity === undefined || !capacityUnit) {
    return undefined;
  }
  const gallons = gallonsOf(capacity, capacityUnit);
  if (gallons !== undefined) {
    return gallons > BULK_OVER_GALLONS;
  }
  const unit = capacityUnit.trim().toUpperCase();
  const state = physicalState?.trim().toUpperCase();
  const poundsPer = POUNDS_PER_UNIT[unit];
  if (poundsPer === undefined) {
    return undefined;
  }
  const limit = state === "GAS" ? BULK_OVER_POUNDS_GAS_WATER_CAPACITY : BULK_OVER_POUNDS_SOLID;
  return capacity * poundsPer > limit;
}

/** What `dotMarkingGroupFor` reads. The enum fields are plain strings because
 * the API hands Oak PascalCase GraphQL enums and the lambdas SCREAMING_CASE
 * Prisma ones; both normalise to the same word here. */
export interface DotMarkingContainer {
  containerType: string;
  capacity?: number | null;
  capacityUnit?: string | null;
  physicalState?: string | null;
}

/**
 * Which DOT markings a container takes.
 *
 * Capacity decides it, not the type code: 49 CFR 171.8 defines bulk by how
 * much the packaging holds, and one code covers both sides of the line --
 * "CM" is "Box, carton, case, or roll-off (metal)", which is a metal crate at
 * one end and a roll-off dumpster at the other. A code that names a vehicle
 * or rail car is bulk whatever is recorded, and TP is a portable tank by
 * definition.
 *
 * Undefined for a container type that is not one of the 13 known codes.
 * `WasteContainer.containerType` is a plain String column, so legacy
 * free-text values reach here; they get no DOT marking rather than one
 * guessed from prose. A known code with no usable capacity falls back to what
 * that code has always meant.
 */
export function dotMarkingGroupFor(
  container: DotMarkingContainer | string
): DotMarkingGroup | undefined {
  const input = typeof container === "string" ? { containerType: container } : container;
  const code = input.containerType.trim().toUpperCase();
  if (!isWasteContainerTypeCode(code)) {
    return undefined;
  }
  if (code === "TP" || ALWAYS_BULK.includes(code)) {
    return GROUP_BY_CODE[code];
  }
  const exceeds = exceedsBulkThreshold(input.capacity, input.capacityUnit, input.physicalState);
  if (exceeds === undefined) {
    return GROUP_BY_CODE[code];
  }
  return exceeds ? "BULK" : "PACKAGE";
}

/** The hazard-class diamond, subsidiary diamond, inhalation wording and
 * orientation arrows -- non-bulk packagings only. */
export function requiresHazardLabel(group: DotMarkingGroup): boolean {
  return group === "PACKAGE";
}

/** The 3.9in identification number on a contrasting background. */
export function requiresLargeIdNumber(group: DotMarkingGroup): boolean {
  return group === "BULK" || group === "PORTABLE_TANK";
}

/** The proper shipping name on two opposing sides -- portable tanks only. */
export function requiresShippingNameOnOpposingSides(group: DotMarkingGroup): boolean {
  return group === "PORTABLE_TANK";
}

/**
 * 49 CFR 172.302(a): the identification number goes on each side and each end
 * at 3,785 L (1,000 gallons) or more, and on two opposing sides below that.
 * A capacity we cannot read in gallons takes the larger marking -- marking
 * more than the section asks is permitted, marking less is not.
 */
const ID_NUMBER_EVERY_SIDE_OVER_GALLONS = 1000;

/** 49 CFR 172.313(a): a bulk packaging shows the inhalation marking on two
 * opposing sides. */
export const INHALATION_MARKING_INSTRUCTION = `Display ${INHALATION_HAZARD_MARKING} on two opposing sides.`;

/**
 * What the handler has to do with the ID-number marking, by group.
 *
 * On the marking itself rather than in a help article: the marking sheet is
 * what physically travels to the container, and the person holding it is the
 * person who decides where it goes. Shared rather than written at the render
 * site because Oak's preview shows the same sentence in a notice (the
 * preview renders one page and cannot show the marking page inline).
 */
export function bulkMarkingInstruction(
  group: DotMarkingGroup,
  container?: DotMarkingContainer,
  hasInhalationMarking = false
): string {
  const gallons = gallonsOf(container?.capacity, container?.capacityUnit);
  const twoSides = gallons !== undefined && gallons < ID_NUMBER_EVERY_SIDE_OVER_GALLONS;
  const idNumber = twoSides
    ? "Display this ID number on two opposing sides of the container."
    : "Display this ID number on each side and each end of the container.";
  const withName = requiresShippingNameOnOpposingSides(group)
    ? `${idNumber} Display the proper shipping name on two opposing sides.`
    : idNumber;
  return hasInhalationMarking ? `${withName} ${INHALATION_MARKING_INSTRUCTION}` : withName;
}

/**
 * What the handler has to do with the printed hazard label(s), for a PACKAGE
 * container -- 49 CFR 172.406(c). Shared with `bulkMarkingInstruction` above:
 * the PDF and Oak's preview both read this rather than writing their own
 * copy, so the two surfaces cannot say something different.
 */
export function packageLabelInstruction(printsBothLabels: boolean): string {
  // Kept to one rendered line. The longer phrasing ran to three and the last
  // one was clipped by the QR code beneath it.
  return printsBothLabels
    ? "Apply both labels to the package, within 150 mm of each other."
    : "Apply the printed label to the package.";
}
